// ============================================
// PAGE - Suscripción
// ============================================

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from '@almacenes/ui';
import { 
  useAuthStore, 
  useSession, 
  flowRepository, 
  type FlowCustomer, 
  type FlowSubscription, 
  type FlowInvoice,
} from '@almacenes/core';
import { FaCreditCard } from "react-icons/fa6";
import { useQueryClient } from '@tanstack/react-query';
import logo from '@/assets/logos/logo.svg';

export const SuscripcionPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session: cachedSession, logout } = useAuthStore();
  const { data: session } = useSession();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingFlow, setIsLoadingFlow] = useState(true);
  const [createOrderError, setCreateOrderError] = useState<string | null>(null);
  
  // Estados para datos de Flow
  const [flowCustomer, setFlowCustomer] = useState<FlowCustomer | null>(null);
  const [flowSubscription, setFlowSubscription] = useState<FlowSubscription | null>(null);
  const [flowInvoice, setFlowInvoice] = useState<FlowInvoice | null>(null);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  
  // Estados para polling de pago manual
  const [isWaitingPayment, setIsWaitingPayment] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0); // En segundos
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [isPollingActive, setIsPollingActive] = useState(true); // Control de polling automático

  // Ref para evitar múltiples llamadas con el mismo RUT
  const lastLoadedRut = useRef<string | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Usar session del servidor o caché como fallback
  const effectiveSession = session || cachedSession;
  const rutAlmacen = effectiveSession?.almacen?.rut;

  // Cargar datos de Flow al montar el componente
  useEffect(() => {
    const loadFlowData = async () => {
      console.log('[SuscripcionPage] useEffect ejecutándose');
      console.log('[SuscripcionPage] rutAlmacen:', rutAlmacen);
      console.log('[SuscripcionPage] lastLoadedRut:', lastLoadedRut.current);
      
      // Si no hay RUT o ya se cargó este mismo RUT, no hacer nada
      if (!rutAlmacen) {
        console.log('[SuscripcionPage] No hay rutAlmacen, mostrando opciones de pago');
        setIsLoadingFlow(false);
        setShowPaymentOptions(true);
        return;
      }

      // Evitar cargar el mismo RUT múltiples veces
      if (lastLoadedRut.current === rutAlmacen) {
        console.log('[SuscripcionPage] Ya se cargaron los datos para este RUT, saltando...');
        return;
      }

      try {
        setIsLoadingFlow(true);
        console.log('[SuscripcionPage] Iniciando carga de datos de Flow...');
        
        // 1. Consultar si existe el cliente en Flow
        const customer = await flowRepository.getCustomer(rutAlmacen);
        console.log('[SuscripcionPage] Customer recibido:', customer);
        
        // Si el status es 200 y hay subscriptionId
        if (customer.status === 200 && customer.subscriptionId) {
          setFlowCustomer(customer);
          
          // 2. Obtener datos de la suscripción
          const subscription = await flowRepository.getSubscription(customer.subscriptionId);
          console.log('[SuscripcionPage] Subscription recibida:', subscription);
          setFlowSubscription(subscription);
          
          // 3. Obtener datos de la factura
          const invoice = await flowRepository.getInvoice(subscription.invoiceId);
          console.log('[SuscripcionPage] Invoice recibida:', invoice);
          setFlowInvoice(invoice);
          
          setShowPaymentOptions(false);
        } else {
          // Status 404 o subscriptionId null - mostrar opciones de pago
          console.log('[SuscripcionPage] Status no es 200 o sin subscriptionId, mostrando opciones');
          setShowPaymentOptions(true);
        }

        // Marcar este RUT como ya cargado
        lastLoadedRut.current = rutAlmacen;
      } catch (error: any) {
        console.error('[SuscripcionPage] Error cargando datos de Flow:', error);
        console.error('[SuscripcionPage] Error response:', error?.response);
        console.error('[SuscripcionPage] Error status:', error?.response?.status);
        
        // Si es 404, mostrar opciones de pago
        if (error?.response?.status === 404) {
          console.log('[SuscripcionPage] Error 404, mostrando opciones de pago');
          setShowPaymentOptions(true);
        } else {
          // Otro error - mostrar opciones por defecto
          console.log('[SuscripcionPage] Otro error, mostrando opciones de pago');
          setShowPaymentOptions(true);
        }

        // Marcar como cargado incluso con error para no reintentar
        lastLoadedRut.current = rutAlmacen;
      } finally {
        setIsLoadingFlow(false);
      }
    };

    loadFlowData();
  }, [rutAlmacen]);

  // Polling para verificar pago después de abrir ventana de Flow
  useEffect(() => {
    if (!isWaitingPayment || !rutAlmacen) {
      // Limpiar intervalos si se desactiva
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
        timeIntervalRef.current = null;
      }
      return;
    }

    console.log('[SuscripcionPage] Iniciando polling de pago manual...');

    // Reset de estados
    setElapsedTime(0);
    setIsPollingActive(true);

    // Intervalo para actualizar tiempo cada segundo
    timeIntervalRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    // Intervalo de polling cada 5 segundos
    pollingIntervalRef.current = setInterval(async () => {
      setElapsedTime(current => {
        // Si ya pasaron 15 minutos (900 segundos), detener polling automático
        if (current >= 900) {
          console.log('[SuscripcionPage] Timeout de 15 minutos alcanzado, deteniendo polling automático...');
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsPollingActive(false);
          return current;
        }
        return current;
      });

      try {
        console.log('[SuscripcionPage] Verificando estado de pago...');

        const statusResponse = await flowRepository.getSubscriptionStatus(rutAlmacen);
        console.log('[SuscripcionPage] Estado recibido:', statusResponse);

        if (statusResponse.status === 'ACCEPTED') {
          console.log('[SuscripcionPage] ¡Pago confirmado! Invalidando sesión...');
          
          // Limpiar intervalos
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          if (timeIntervalRef.current) {
            clearInterval(timeIntervalRef.current);
            timeIntervalRef.current = null;
          }
          
          // Invalidar sesión para recargar con datos actualizados
          await queryClient.invalidateQueries({ queryKey: ['session'] });
          
          // Recargar datos de Flow
          setIsWaitingPayment(false);
          lastLoadedRut.current = null; // Forzar recarga
          window.location.reload(); // Recargar para mostrar estado actualizado
        }
      } catch (error) {
        console.error('[SuscripcionPage] Error verificando estado de pago:', error);
      }
    }, 10000); // Verificar cada 10 segundos

    // Limpiar intervalos al desmontar o cambiar estado
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
        timeIntervalRef.current = null;
      }
    };
  }, [isWaitingPayment, rutAlmacen, queryClient]);

  const handleRegisterCreditCardAuto = async () => {
    if (!effectiveSession?.almacen?.rut || !effectiveSession?.user?.nombre || !effectiveSession?.user?.email) {
      console.error('[SuscripcionPage] Faltan datos de sesión para registrar tarjeta');
      setCreateOrderError('No se pudo obtener la información necesaria. Por favor, recarga la página.');
      return;
    }

    setIsProcessing(true);
    setCreateOrderError(null);
    
    try {
      // Preparar el RUT sin DV (ej: "17728133-6" -> 17728133)
      const rutSinDv = parseInt(effectiveSession.almacen.rut.split('-')[0], 10);
      
      console.log('[SuscripcionPage] Registrando tarjeta con cargo automático...');
      
      // Llamar al endpoint para registrar tarjeta con processPayment=true
      const response = await flowRepository.registerCreditCard({
        rut: rutSinDv,
        name: effectiveSession.user.nombre,
        email: effectiveSession.user.email,
        processPayment: true, // Con cargo automático
        hasDashboardAccess: false,
      });

      console.log('[SuscripcionPage] Respuesta de registro de tarjeta:', response);

      // Redirigir a Flow con token
      const flowUrl = `${response.url}?token=${response.token}`;
      console.log('[SuscripcionPage] Redirigiendo a Flow:', flowUrl);
      window.location.href = flowUrl;
    } catch (error: any) {
      console.error('[SuscripcionPage] Error al registrar tarjeta:', error);
      setCreateOrderError('Ocurrió un error al procesar tu solicitud. Por favor, intenta nuevamente más tarde.');
      setIsProcessing(false);
    }
  };

  const handleRegisterCreditCardOnly = async () => {
    if (!effectiveSession?.almacen?.rut || !effectiveSession?.user?.nombre || !effectiveSession?.user?.email) {
      console.error('[SuscripcionPage] Faltan datos de sesión para registrar tarjeta');
      setCreateOrderError('No se pudo obtener la información necesaria. Por favor, recarga la página.');
      return;
    }

    setIsProcessing(true);
    setCreateOrderError(null);
    
    try {
      // Preparar el RUT sin DV (ej: "17728133-6" -> 17728133)
      const rutSinDv = parseInt(effectiveSession.almacen.rut.split('-')[0], 10);
      
      console.log('[SuscripcionPage] Registrando tarjeta sin cargo (solo registro)...');
      
      // Llamar al endpoint para registrar tarjeta con processPayment=false
      const response = await flowRepository.registerCreditCard({
        rut: rutSinDv,
        name: effectiveSession.user.nombre,
        email: effectiveSession.user.email,
        processPayment: false, // Sin cargo, solo registro
        hasDashboardAccess: false,
      });

      console.log('[SuscripcionPage] Respuesta de registro de tarjeta:', response);

      // Redirigir a Flow con token
      const flowUrl = `${response.url}?token=${response.token}`;
      console.log('[SuscripcionPage] Redirigiendo a Flow:', flowUrl);
      window.location.href = flowUrl;
    } catch (error: any) {
      console.error('[SuscripcionPage] Error al registrar tarjeta:', error);
      setCreateOrderError('Ocurrió un error al procesar tu solicitud. Por favor, intenta nuevamente más tarde.');
      setIsProcessing(false);
    }
  };

  const handleManualSubscription = async () => {
    if (!effectiveSession?.almacen?.rut || !effectiveSession?.user?.nombre || !effectiveSession?.user?.email) {
      console.error('[SuscripcionPage] Faltan datos de sesión para crear suscripción');
      setCreateOrderError('No se pudo obtener la información necesaria. Por favor, recarga la página.');
      return;
    }

    setIsProcessing(true);
    setCreateOrderError(null);
    
    try {
      // Preparar el RUT sin DV (ej: "17728133-6" -> 17728133)
      const rutSinDv = parseInt(effectiveSession.almacen.rut.split('-')[0], 10);
      
      // Obtener planId de variable de entorno
      const planId = parseInt(import.meta.env.VITE_FLOW_PLAN_ID || '11', 10);
      
      console.log('[SuscripcionPage] Creando suscripción manual...');
      console.log('[SuscripcionPage] RUT:', rutSinDv);
      console.log('[SuscripcionPage] Plan ID:', planId);
      
      // Llamar al endpoint para crear suscripción manual
      const response = await flowRepository.createSubscription({
        rutAlmacen: rutSinDv,
        planId: planId,
        customerName: effectiveSession.user.nombre,
        customerEmail: effectiveSession.user.email,
      });

      console.log('[SuscripcionPage] Respuesta de creación de suscripción:', response);

      // Si el status es 200, redirigir a página de espera
      if (response.status === 200) {
        console.log('[SuscripcionPage] Suscripción creada exitosamente, redirigiendo a espera...');
        navigate(`/suscripcion/espera?rut=${effectiveSession.almacen.rut}`);
      } else {
        // Status diferente de 200 - mostrar error
        setCreateOrderError(response.message || 'No se pudo crear la suscripción. Por favor, intenta nuevamente.');
        setIsProcessing(false);
      }
    } catch (error: any) {
      console.error('[SuscripcionPage] Error al crear suscripción:', error);
      setCreateOrderError('Ocurrió un error al procesar tu solicitud. Por favor, intenta nuevamente más tarde.');
      setIsProcessing(false);
    }
  };

  const handleManualPayment = () => {
    if (flowInvoice?.paymentLink) {
      console.log('[SuscripcionPage] Abriendo ventana de pago y activando polling...');
      window.open(flowInvoice.paymentLink, '_blank');
      
      // Activar estado de espera de pago
      setIsWaitingPayment(true);
      setElapsedTime(0);
      setIsPollingActive(true);
    }
  };

  const handleCheckPaymentManually = async () => {
    if (!rutAlmacen) return;

    setIsCheckingPayment(true);
    try {
      console.log('[SuscripcionPage] Verificación manual de pago...');
      const statusResponse = await flowRepository.getSubscriptionStatus(rutAlmacen);
      console.log('[SuscripcionPage] Estado recibido:', statusResponse);

      if (statusResponse.status === 'ACCEPTED') {
        console.log('[SuscripcionPage] ¡Pago confirmado! Invalidando sesión...');
        
        // Invalidar sesión para recargar con datos actualizados
        await queryClient.invalidateQueries({ queryKey: ['session'] });
        
        // Recargar datos de Flow
        setIsWaitingPayment(false);
        lastLoadedRut.current = null; // Forzar recarga
        window.location.reload(); // Recargar para mostrar estado actualizado
      } else {
        // Mostrar mensaje de que aún no se confirma el pago
        alert('El pago aún no ha sido confirmado. Por favor, completa el proceso en la ventana de Flow e intenta nuevamente.');
      }
    } catch (error) {
      console.error('[SuscripcionPage] Error en verificación manual:', error);
      alert('Ocurrió un error al verificar el estado del pago. Por favor, intenta nuevamente.');
    } finally {
      setIsCheckingPayment(false);
    }
  };

  const handleCancelPaymentWait = () => {
    console.log('[SuscripcionPage] Cancelando espera de pago...');
    setIsWaitingPayment(false);
    setElapsedTime(0);
    setIsPollingActive(true);
  };

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };

  const handleGoToDashboard = async () => {
    // Invalidar la sesión para que se recargue con datos actualizados
    await queryClient.invalidateQueries({ queryKey: ['session'] });
    
    // Pequeño delay para asegurar que la sesión se actualice
    setTimeout(() => {
      navigate('/dashboard');
    }, 300);
  };

  // Formatear monto según moneda
  const formatAmount = (amount: string, currency: string): string => {
    const numericAmount = parseFloat(amount);
    
    if (currency === 'UF') {
      // UF: mostrar con 3 decimales, coma como separador decimal
      return numericAmount.toFixed(3).replace('.', ',');
    } else if (currency === 'CLP' || currency === '$') {
      // CLP: mostrar en miles con punto como separador, sin decimales
      return Math.round(numericAmount).toLocaleString('es-CL');
    }
    
    // Por defecto, devolver el valor original
    return amount;
  };

  // Formatear fecha de dd-mm-yyyy
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Formatear tiempo transcurrido en minutos y segundos
  const formatElapsedTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Obtener texto del estado de suscripción
  const getStatusText = (status: 0 | 1 | 2): string => {
    switch (status) {
      case 0:
        return 'Impago';
      case 1:
        return 'Pagado';
      case 2:
        return 'Anulado';
      default:
        return 'Desconocido';
    }
  };

  // Obtener color del estado
  const getStatusColor = (status: 0 | 1 | 2): string => {
    switch (status) {
      case 0:
        return '#EF4444'; // Rojo
      case 1:
        return '#10B981'; // Verde
      case 2:
        return '#6B7280'; // Gris
      default:
        return '#6B7280';
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F3F4F9' }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between">
        <img 
          src={logo} 
          alt="Almacenes Digitales" 
          className="h-9"
        />
        <Button
          variant="danger"
          onClick={handleLogout}
          size="sm"
        >
          Cerrar sesión
        </Button>
      </div>

      {/* Contenido */}
      <div className="flex-1 flex items-center justify-center px-8 py-4">
        <div className="max-w-6xl w-full">
          {isLoadingFlow ? (
            // Loading state
            <div className="flex flex-col items-center justify-center py-20">
              <Spinner size="lg" />
              <p className="mt-4 text-gray-600">Cargando información de suscripción...</p>
            </div>
          ) : !effectiveSession?.user?.esPropietario ? (
            // No es propietario - Acceso restringido
            <div className="flex flex-col items-center justify-center py-20">
              <div className="text-center max-w-2xl">
                <div className="text-8xl mb-8">🔒</div>
                <h1 className="text-4xl font-bold mb-6" style={{ color: '#11194F' }}>
                  Acceso Restringido
                </h1>
                <p className="text-gray-600 text-xl mb-8 leading-relaxed">
                  Solo el propietario de este almacén puede realizar la gestión de pagos y suscripciones.
                </p>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-8">
                  <p className="text-blue-800 font-semibold mb-2">
                    👤 Usuario actual: {effectiveSession?.user?.nombre}
                  </p>
                  <p className="text-blue-700 text-sm">
                    Para gestionar la suscripción, solicita al propietario que inicie sesión.
                  </p>
                </div>
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Almacén: <span className="font-semibold">{effectiveSession?.almacen.nombre}</span>
                  </p>
                </div>
              </div>
            </div>
          ) : showPaymentOptions ? (
            // Mostrar opciones de pago (status 404 o subscriptionId null)
            <>
              <div className="text-center mb-12">
                <div className="text-6xl mb-6">🚀</div>
                <h1 className="text-4xl font-bold mb-4" style={{ color: '#11194F' }}>
                  ¡Activa tu cuenta!
                </h1>
                <p className="text-gray-600 text-xl">
                  Elige una de las opciones de suscripción para comenzar a disfrutar de todas las funcionalidades de Almacenes Digitales.
                </p>
              </div>

              {/* Cards de planes */}
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Plan Premium Automático - RECOMENDADO */}
                <div className="bg-white rounded-3xl shadow-xl p-8 border-2 relative" style={{ borderColor: '#43BFFF' }}>
                  {/* Badge Recomendado */}
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="px-6 py-2 rounded-full text-white text-sm font-bold" style={{ backgroundColor: '#43BFFF' }}>
                      RECOMENDADO
                    </div>
                  </div>
                  
                  <div className="text-center mb-4 mt-2">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                      Premium Mensual + Cargo Automático
                    </h3>
                    <div className="flex items-baseline justify-center mb-2">
                      <span className="text-5xl font-bold" style={{ color: '#11194F' }}>
                        $26.990 + IVA
                      </span>
                      <span className="text-xl text-gray-500 ml-2">/Mes</span>
                    </div>
                    <p className="text-sm text-gray-600 px-4 leading-relaxed">
                      Registra tu tarjeta una sola vez y nosotros haremos el cobro automático cada mes. Sin interrupciones ni recordatorios, tu suscripción siempre activa.
                    </p>
                  </div>

                  <ul className="space-y-2.5 mb-8 mt-6">
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Inventario</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Venta</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Reporte</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Fiados</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Detalle Boleta</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Ecommerce</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Boleta Electronica</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Resumen SII</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Cuadratura Caja</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Configuraciones</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">...y otras funcionalidades avanzadas</span>
                    </li>
                  </ul>

                  <Button
                    variant="primary"
                    className="w-full py-4 text-lg font-semibold"
                    size="lg"
                    onClick={handleRegisterCreditCardAuto}
                    isLoading={isProcessing}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <FaCreditCard className="text-xl" />
                      <span>REGISTRA TARJETA</span>
                    </span>
                  </Button>
                </div>

                {/* Plan Premium Manual */}
                <div className="bg-white rounded-3xl shadow-lg p-8 border-2 border-gray-200">
                  <div className="text-center mb-4 mt-2">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                      Premium Mensual
                    </h3>
                    <div className="flex items-baseline justify-center mb-2">
                      <span className="text-5xl font-bold" style={{ color: '#11194F' }}>
                        $26.990 + IVA
                      </span>
                      <span className="text-xl text-gray-500 ml-2">/Mes</span>
                    </div>
                    <p className="text-sm text-gray-600 px-4 leading-relaxed">
                      Recibirás un correo electrónico con el link de pago cada mes para renovar tu suscripción.
                    </p>
                  </div>

                  <ul className="space-y-2.5 mb-8 mt-6">
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Inventario</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Venta</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Reporte</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Fiados</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Detalle Boleta</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Ecommerce</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Boleta Electronica</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Resumen SII</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Cuadratura Caja</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">Configuraciones</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-500 text-lg font-bold">✓</span>
                      <span className="text-gray-800">...y otras funcionalidades avanzadas</span>
                    </li>
                  </ul>

                  <Button
                    variant="primary"
                    className="w-full py-4 text-lg font-semibold"
                    size="lg"
                    onClick={handleManualSubscription}
                    isLoading={isProcessing}
                  >
                    PAGAR
                  </Button>
                </div>
              </div>

              {/* Mensaje de error si falla la creación de la orden */}
              {createOrderError && (
                <div className="bg-red-50 border-2 border-red-400 rounded-xl p-6 mb-8">
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">❌</span>
                    <div>
                      <p className="text-red-800 font-semibold mb-1">
                        Error al Procesar
                      </p>
                      <p className="text-red-700 text-sm">
                        {createOrderError}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="text-center">
                <p className="text-gray-600">
                  Usuario: <span className="font-semibold">{effectiveSession?.user.nombre}</span>
                </p>
                <p className="text-gray-600">
                  Almacén: <span className="font-semibold">{effectiveSession?.almacen.nombre}</span>
                </p>
              </div>
            </>
          ) : (
            // Mostrar información de suscripción activa
            <>
              <div className="text-center mb-8">
                <div className="text-6xl mb-6">💳</div>
                <h1 className="text-4xl font-bold mb-4" style={{ color: '#11194F' }}>
                  Información de Suscripción
                </h1>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Información del Cliente */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                  <h2 className="text-xl font-bold mb-4" style={{ color: '#11194F' }}>
                    Datos del Cliente
                  </h2>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">Nombre</p>
                      <p className="text-lg font-semibold text-gray-800">{flowCustomer?.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="text-lg font-semibold text-gray-800">{flowCustomer?.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Modo de Pago</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {flowCustomer?.payMode === 'auto' ? 'Automático' : 'Manual'}
                      </p>
                    </div>
                    {flowCustomer?.creditCardType ? (
                      <div>
                        <p className="text-sm text-gray-500">Tarjeta</p>
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-lg font-semibold text-gray-800">
                            {flowCustomer.creditCardType} **** {flowCustomer.last4CardDigits}
                          </p>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleRegisterCreditCardOnly}
                            isLoading={isProcessing}
                          >
                            Cambiar tarjeta
                          </Button>
                        </div>
                      </div>
                    ) : (
                      flowCustomer?.payMode !== 'auto' && !isWaitingPayment && (
                        <div>
                          <p className="text-sm text-gray-500">Tarjeta</p>
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-base text-gray-600">No registrada</p>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={handleRegisterCreditCardOnly}
                              isLoading={isProcessing}
                              style={{ backgroundColor: '#10B981', borderColor: '#10B981' }}
                            >
                              <span className="flex items-center gap-2">
                                <FaCreditCard />
                                Registrar tarjeta
                              </span>
                            </Button>
                          </div>
                        </div>
                      )
                    )}
                    <div>
                      <p className="text-sm text-gray-500">Fecha de Registro</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {flowCustomer?.registerDate && formatDate(flowCustomer.registerDate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Estado de Suscripción</p>
                      <p className={`text-lg font-semibold ${
                        flowCustomer?.subscriptionStatus === 'ACTIVA' 
                          ? 'text-green-600' 
                          : flowCustomer?.subscriptionStatus === 'CANCELADA'
                          ? 'text-red-600'
                          : 'text-gray-600'
                      }`}>
                        {flowCustomer?.subscriptionStatus}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Información del Último Importe */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                  <h2 className="text-xl font-bold mb-4" style={{ color: '#11194F' }}>
                    Último Importe
                  </h2>

                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">Monto</p>
                      <p className="text-3xl font-bold" style={{ color: '#11194F' }}>
                        {flowSubscription?.currency} {flowSubscription && formatAmount(flowSubscription.amount, flowSubscription.currency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Fecha de Vencimiento</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {flowSubscription?.dueDate && formatDate(flowSubscription.dueDate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Estado</p>
                      <p 
                        className="text-lg font-semibold inline-block px-4 py-2 rounded-full"
                        style={{ 
                          backgroundColor: `${getStatusColor(flowSubscription?.status ?? 0)}20`,
                          color: getStatusColor(flowSubscription?.status ?? 0)
                        }}
                      >
                        {getStatusText(flowSubscription?.status ?? 0)}
                      </p>
                    </div>

                    {/* Botón para ir al dashboard si el pago está confirmado */}
                    {flowSubscription?.status === 1 && (
                      <div className="mt-6 p-4 bg-green-50 border-2 border-green-400 rounded-xl">
                        <div className="flex items-start gap-3 mb-4">
                          <span className="text-2xl">✅</span>
                          <div>
                            <p className="text-sm font-semibold text-green-800 mb-1">
                              Pago Confirmado
                            </p>
                            <p className="text-sm text-green-700">
                              Tu suscripción está activa. Puedes continuar al dashboard para acceder a todas las funcionalidades premium.
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="primary"
                          className="w-full shadow-lg"
                          size="lg"
                          onClick={handleGoToDashboard}
                        >
                          <span className="font-bold">IR AL DASHBOARD</span>
                        </Button>
                      </div>
                    )}

                    {/* Botón de pago manual si existe paymentLink */}
                    {flowInvoice?.paymentLink && !isWaitingPayment && (
                      <div className="mt-6 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-xl">
                        <div className="flex items-start gap-3 mb-4">
                          <span className="text-2xl">⚠️</span>
                          <div>
                            <p className="text-sm font-semibold text-yellow-800 mb-1">
                              Acción Requerida
                            </p>
                            <p className="text-sm text-yellow-700">
                              Debido a los intentos de cobro fallidos, necesitamos que completes el pago manualmente haciendo clic en el siguiente botón.
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="primary"
                          className="w-full shadow-lg"
                          size="lg"
                          onClick={handleManualPayment}
                        >
                          <span className="flex items-center justify-center gap-2">
                            <FaCreditCard className="text-xl" />
                            <span className="font-bold">PAGAR AQUÍ</span>
                          </span>
                        </Button>
                      </div>
                    )}

                    {/* UI de espera después de abrir ventana de pago */}
                    {isWaitingPayment && (
                      <div className="mt-6 p-6 bg-blue-50 border-2 border-blue-400 rounded-xl">
                        <div className="flex items-start gap-3 mb-4">
                          {isPollingActive ? (
                            <div className="flex-shrink-0">
                              <Spinner size="md" />
                            </div>
                          ) : (
                            <span className="text-2xl">⏱️</span>
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-blue-800 mb-2">
                              {isPollingActive 
                                ? 'Esperando Confirmación de Pago' 
                                : 'Tiempo de Espera Automático Agotado'}
                            </p>
                            <p className="text-sm text-blue-700 mb-3">
                              {isPollingActive
                                ? 'Una vez que completes el pago en la ventana de Flow, tu suscripción se activará automáticamente. También puedes verificar manualmente haciendo clic en el botón de abajo.'
                                : 'Han transcurrido más de 15 minutos. La verificación automática se ha detenido, pero puedes continuar verificando manualmente haciendo clic en el botón "YA PAGUÉ".'}
                            </p>
                            <div className="flex items-center gap-2">
                              <p className="text-xs text-blue-600 font-mono">
                                Tiempo transcurrido: {formatElapsedTime(elapsedTime)}
                              </p>
                              {elapsedTime >= 900 && (
                                <span className="text-xs text-orange-600 font-semibold">(Verificación automática detenida)</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Button
                            variant="primary"
                            className="w-full"
                            size="lg"
                            onClick={handleCheckPaymentManually}
                            isLoading={isCheckingPayment}
                          >
                            <span className="font-bold">YA PAGUÉ</span>
                          </Button>
                          <Button
                            variant="secondary"
                            className="w-full"
                            size="lg"
                            onClick={handleCancelPaymentWait}
                            disabled={isCheckingPayment}
                          >
                            <span className="font-bold">CANCELAR</span>
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Lista de intentos de cobro fallidos */}
              {flowInvoice?.chargeAttempts && flowInvoice.chargeAttempts.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 mb-8">
                  <h2 className="text-xl font-bold mb-4" style={{ color: '#11194F' }}>
                    Intentos de Cobro Fallidos
                  </h2>
                  <div className="space-y-3">
                    {flowInvoice.chargeAttempts.map((attempt, index) => (
                      <div 
                        key={index} 
                        className="flex items-start gap-4 p-4 bg-red-50 border border-red-200 rounded-lg"
                      >
                        <div className="flex-shrink-0">
                          <span className="text-red-500 text-2xl">⚠️</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-500">
                            {formatDate(attempt.date)}
                          </p>
                          <p className="text-base font-semibold text-red-700">
                            Error {attempt.errorCode}: {attempt.errorDescription}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-center">
                <p className="text-gray-600">
                  Usuario: <span className="font-semibold">{effectiveSession?.user.nombre}</span>
                </p>
                <p className="text-gray-600">
                  Almacén: <span className="font-semibold">{effectiveSession?.almacen.nombre}</span>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
